import type { DisplayPoint, DisplayPointRange, DisplayPointItem } from './types';
import type { CatalogEntry } from './equipmentCatalog';
import React from 'react'
import haystackProto from '../../scripts/haystack_proto_points.json'

type HaystackOption = {
    category: string;
    tag: string;
    description: string;
    unit: string;
    kind: string;
};

function buildHaystackOptions(proto: any): HaystackOption[] {
    const out: HaystackOption[] = [];

    for (const [catKey, cat] of Object.entries(proto.categories)) {
        const categoryName = catKey;
        const points = (cat as any).points;

        for (const [tag, def] of Object.entries(points)) {
            out.push({
                category: categoryName,
                tag,
                description: (def as any).description,
                unit: (def as any).unit,
                kind: (def as any).kind
            });
        }
    }

    return out;
}

const haystackOptions = buildHaystackOptions(haystackProto);

function GroupedMultiSelect({
                                options,
                                value,
                                onChange,
                                placeholder = "Select...",
                            }: {
    options: { label: string; value: string; category: string }[];
    value: string[];
    onChange: (next: string[]) => void;
    placeholder?: string;
}) {
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState("");

    const filtered = options.filter(opt =>
        opt.label.toLowerCase().includes(query.toLowerCase())
    );

    const grouped = filtered.reduce((acc, opt) => {
        if (!acc[opt.category]) acc[opt.category] = [];
        acc[opt.category].push(opt);
        return acc;
    }, {} as Record<string, typeof options>);

    const toggle = (val: string) => {
        onChange(
            value.includes(val)
                ? value.filter(v => v !== val)
                : [...value, val]
        );
    };

    return (
        <div className="custom-multiselect">
            {/* Selected chips */}
            <div className="multi-value" onClick={() => setOpen(o => !o)}>
                {value.length === 0 && (
                    <span className="placeholder">{placeholder}</span>
                )}
                {value.map(v => {
                    const opt = options.find(o => o.value === v);
                    return (
                        <span key={v} className="chip">
              {opt?.label ?? v}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggle(v);
                                }}
                            >
                ×
              </button>
            </span>
                    );
                })}
            </div>

            {open && (
                <div className="dropdown">
                    <input
                        className="search"
                        placeholder="Search…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />

                    <div className="options">
                        {Object.entries(grouped).map(([category, items]) => (
                            <div key={category} className="group">
                                <div className="group-label">{category}</div>

                                {items.map(opt => (
                                    <div
                                        key={opt.value}
                                        className={`option ${value.includes(opt.value) ? "selected" : ""}`}
                                        onClick={() => toggle(opt.value)}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={value.includes(opt.value)}
                                            readOnly
                                        />
                                        {opt.label}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}


interface DisplayPointEditorProps {
  isOpen: boolean;
  editingPoint: DisplayPointItem | null;
  form: DisplayPoint;
  isLoading: boolean;
  catalogEntries: CatalogEntry[];
  onFormChange: (form: DisplayPoint) => void;
  onSave: () => void;
  onClose: () => void;
}

export function DisplayPointEditor({
  isOpen,
  editingPoint,
  form,
  isLoading,
  catalogEntries,
  onFormChange,
  onSave,
  onClose,
}: DisplayPointEditorProps) {
  if (!isOpen) return null;

    const equipmentByCategory = catalogEntries.reduce((acc, entry) => {
        if (!acc[entry.category]) acc[entry.category] = [];
        acc[entry.category].push(entry);
        return acc;
    }, {} as Record<string, CatalogEntry[]>);

    const equipmentOptions = catalogEntries.map(entry => ({
        label: `${entry.typeName} (${entry.typeCode})`,
        value: entry.typeName,
        category: entry.category,
    }));


  const updateRange = (index: number, field: keyof DisplayPointRange, value: string | number) => {
    onFormChange({
      ...form,
      ranges: form.ranges.map((r, i) =>
        i === index ? { ...r, [field]: value } : r
      ),
    });
  };

  const addRange = () => {
    onFormChange({
      ...form,
      ranges: [
        ...form.ranges,
        { name: 'New Range', from: 0, to: 100, color: '#8bc34a', feeling: 0 },
      ],
    });
  };

  const removeRange = (index: number) => {
    if (form.ranges.length <= 1) return;
    onFormChange({
      ...form,
      ranges: form.ranges.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <i className="fas fa-edit"></i>
            {editingPoint ? 'Edit Display Point' : 'Add Display Point'}
          </h2>
          <button className="btn-icon" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group">
              <label>Point Name (ID)*</label>
              <input
                type="text"
                value={form.pointName}
                onChange={(e) => onFormChange({ ...form, pointName: e.target.value })}
                placeholder="e.g., HC_Energy_kW"
                disabled={!!editingPoint}
              />
            </div>
            <div className="form-group">
              <label>Display Name*</label>
              <input
                type="text"
                value={form.displayName}
                onChange={(e) => onFormChange({ ...form, displayName: e.target.value })}
                placeholder="e.g., Energy Consumption"
              />
            </div>
            <div className="form-group full-width">
              <label>Description</label>
              <textarea
                value={form.description}
                onChange={(e) => onFormChange({ ...form, description: e.target.value })}
                placeholder="Describe what this point measures..."
                rows={2}
              />
            </div>
            <div className="form-group">
              <label>Unit</label>
              <input
                type="text"
                value={form.unit}
                onChange={(e) => onFormChange({ ...form, unit: e.target.value })}
                placeholder="e.g., kW, °F, GPM"
              />
            </div>
            <div className="form-group">
              <label>Aggregation</label>
              <select
                value={form.aggregation}
                onChange={(e) => onFormChange({ ...form, aggregation: e.target.value })}
              >
                <option value="avg">Average (avg)</option>
                <option value="sum">Sum (sum)</option>
                <option value="min">Minimum (min)</option>
                <option value="max">Maximum (max)</option>
                <option value="count">Count (count)</option>
              </select>
            </div>
            <div className="form-group">
              <label>Decimal Places</label>
              <input
                type="number"
                value={form.places}
                onChange={(e) => onFormChange({ ...form, places: parseInt(e.target.value) || 0 })}
                min={0}
                max={6}
              />
            </div>
            <div className="form-group">
              <label>Icon Class</label>
              <input
                type="text"
                value={form.iconClass}
                onChange={(e) => onFormChange({ ...form, iconClass: e.target.value })}
                placeholder="e.g., fa-solid fa-bolt"
              />
            </div>
              <div className="form-group full-width">
                  <label>Equipment Type(s)</label>

                  <GroupedMultiSelect
                      options={equipmentOptions}
                      value={form.dtTypes}
                      onChange={(next) =>
                          onFormChange({ ...form, dtTypes: next })
                      }
                      placeholder="Select equipment types…"
                  />

                  {catalogEntries.length === 0 && (
                      <div className="helper-text">
                          No equipment catalog entries available.
                      </div>
                  )}
              </div>


              <div className="form-group full-width">
                  <label>Haystack Tag*</label>

                  <select
                      value={form.haystackTag || ''}
                      onChange={(e) =>
                          onFormChange({ ...form, haystackTag: e.target.value })
                      }
                  >
                      <option value="" disabled>
                          Select a Haystack point…
                      </option>

                      {Object.entries(
                          haystackOptions.reduce((acc, opt) => {
                              if (!acc[opt.category]) acc[opt.category] = [];
                              acc[opt.category].push(opt);
                              return acc;
                          }, {} as Record<string, HaystackOption[]>)
                      ).map(([category, options]) => (
                          <optgroup key={category} label={category}>
                              {options.map(opt => (
                                  <option key={opt.tag} value={opt.tag}>
                                      {opt.tag} — {opt.description} ({opt.unit})
                                  </option>
                              ))}
                          </optgroup>
                      ))}
                  </select>
              </div>

          </div>

          <div className="ranges-section">
            <div className="ranges-header">
              <h3>Value Ranges</h3>
              <button className="btn-secondary" onClick={addRange}>
                <i className="fas fa-plus"></i>
                Add Range
              </button>
            </div>
            <div className="ranges-list">
              {form.ranges.map((range, idx) => (
                <div key={idx} className="range-row">
                  <input
                    type="text"
                    value={range.name}
                    onChange={(e) => updateRange(idx, 'name', e.target.value)}
                    placeholder="Name"
                    className="range-name"
                  />
                  <input
                    type="number"
                    value={range.from === Number.MIN_SAFE_INTEGER ? '' : range.from}
                    onChange={(e) => updateRange(idx, 'from', e.target.value === '' ? Number.MIN_SAFE_INTEGER : parseFloat(e.target.value))}
                    placeholder="Min"
                    className="range-value"
                  />
                  <span className="range-separator">to</span>
                  <input
                    type="number"
                    value={range.to === Number.MAX_SAFE_INTEGER ? '' : range.to}
                    onChange={(e) => updateRange(idx, 'to', e.target.value === '' ? Number.MAX_SAFE_INTEGER : parseFloat(e.target.value))}
                    placeholder="Max"
                    className="range-value"
                  />
                  <input
                    type="color"
                    value={range.color}
                    onChange={(e) => updateRange(idx, 'color', e.target.value)}
                    className="range-color"
                  />
                  <select
                    value={range.feeling}
                    onChange={(e) => updateRange(idx, 'feeling', parseInt(e.target.value))}
                    className="range-feeling"
                  >
                    <option value={-1}>Bad</option>
                    <option value={0}>Neutral</option>
                    <option value={1}>Good</option>
                  </select>
                  <button
                    className="btn-icon danger"
                    onClick={() => removeRange(idx)}
                    disabled={form.ranges.length <= 1}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={onSave}
            disabled={isLoading || !form.pointName || !form.displayName || !form.haystackTag}
          >
            {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
            {editingPoint ? 'Save Changes' : 'Create Point'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DisplayPointEditor;




